import { Coordinates } from './state'

export interface TransformInput {
	x: number // top-left x
	y: number // top-left y
	w: number // width
	h: number // height
	scale: number // uniform scale
	rotate: number // radians
}

export interface BoundingBox {
	topLeft: Coordinates
	topRight: Coordinates
	bottomLeft: Coordinates
	bottomRight: Coordinates
}

export function getBoundingBox({
	x,
	y,
	w,
	h,
	scale,
	rotate,
}: TransformInput): BoundingBox {
	const cx = x + w / 2
	const cy = y + h / 2

	// define the corners relative to top-left
	const corners: Coordinates[] = [
		{ x, y }, // top-left
		{ x: x + w, y }, // top-right
		{ x, y: y + h }, // bottom-left
		{ x: x + w, y: y + h }, // bottom-right
	]

	// apply transforms
	const transformed = corners.map(({ x: px, y: py }) => {
		// translate to center
		let dx = px - cx
		let dy = py - cy

		// scale
		dx *= scale
		dy *= scale

		// rotate
		const cos = Math.cos(rotate)
		const sin = Math.sin(rotate)

		const rx = dx * cos - dy * sin
		const ry = dx * sin + dy * cos

		// back to cartesian
		return { x: cx + rx, y: cy + ry } as Coordinates
	})

	return {
		topLeft: transformed[0],
		topRight: transformed[1],
		bottomLeft: transformed[2],
		bottomRight: transformed[3],
	}
}

export function getRotationAngle(
	centerPoint: Coordinates,
	startPoint: Coordinates,
	endPoint: Coordinates,
): number {
	const { x: cx, y: cy } = centerPoint

	const startAngle = Math.atan2(startPoint.y - cy, startPoint.x - cx)
	const endAngle = Math.atan2(endPoint.y - cy, endPoint.x - cx)

	let rotationAngle = endAngle - startAngle

	// angle normalization
	while (rotationAngle > Math.PI) {
		rotationAngle -= 2 * Math.PI
	}
	while (rotationAngle < -Math.PI) {
		rotationAngle += 2 * Math.PI
	}

	return rotationAngle
}
export function getScaleDifference(
	centerPoint: Coordinates,
	startPoint: Coordinates,
	endPoint: Coordinates,
): number {
	const distanceStartToCenter = Math.sqrt(
		Math.pow(startPoint.x - centerPoint.x, 2)
			+ Math.pow(startPoint.y - centerPoint.y, 2),
	)

	const distanceEndToCenter = Math.sqrt(
		Math.pow(endPoint.x - centerPoint.x, 2)
			+ Math.pow(endPoint.y - centerPoint.y, 2),
	)

	return distanceEndToCenter / distanceStartToCenter
}
